import { FC } from 'react'
import { ModalSectionProps, RotaPatternModalType } from './utils'
import { Button, PageHeader } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { capitalize } from 'lodash'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { GeneralRotaInformation } from './components'
import { Home, HomeDto, ResidentStatus } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import Spinner from 'LEGACY/Spinner'
import RecurrencePattern from './components/RecurrencePattern'
import TeamMembersAndSkills from './components/TeamMembersAndSkills'
import { useRotaPatternDetail } from '../hooks/useRotaPatternDetail'
import { EditOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { theme } from '@lmc/ui-kit'
import { getNominative } from '@lmc/common/utils'

type RotaPatternViewProps = {
  type: RotaPatternModalType
} & ModalSectionProps

export const RotaPatternView: FC<RotaPatternViewProps> = ({ type }) => {
  const { t } = useTranslation()

  const { id } = useParams()

  const navigate = useNavigate()

  const home: HomeDto | undefined = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  const { patternDetail, isLoadingDetail } = useRotaPatternDetail({
    id,
  })

  const residentStatus =
    type === 'visit' ? patternDetail?.resident?.status : undefined

  const residentId = type === 'visit' ? patternDetail?.resident?.id : undefined

  if (isLoadingDetail) {
    return (
      <LoadingWrapper>
        <Spinner className={undefined} />
      </LoadingWrapper>
    )
  }

  return (
    <>
      <PageHeader
        title={t('THE_PATTERN', { variable: capitalize(type) })}
        onBack={() => navigate(`/rota/patterns?tabKey=${type}s`)}
      >
        <ButtonsWrapper>
          {residentStatus && residentStatus !== ResidentStatus.active ? (
            <Tooltip
              color={theme.color.neutral.color3}
              placement="bottomRight"
              title={
                <TooltipTitle>
                  <Trans
                    i18nKey="SU_INACTIVE_MAKE_ACTIVE"
                    values={{
                      nominative: residentNominative,
                      residentStatus,
                    }}
                    components={{
                      profile: (
                        <StyledLink
                          to={`${window.location.origin}/residents/${residentId}/profile`}
                          target="_blank"
                        />
                      ),
                    }}
                  />
                </TooltipTitle>
              }
            >
              <Button disabled>
                <EditOutlined />
                {t('EDIT')}
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="primary"
              onClick={() => navigate(`/rota/${id}/${type}/edit`)}
              type="submit"
            >
              <EditOutlined />
              {t('EDIT')}
            </Button>
          )}
        </ButtonsWrapper>
      </PageHeader>
      <GeneralRotaInformation
        home={home as HomeDto}
        pattern={patternDetail}
        type={type}
      />
      <DetailsWrapper>
        <RecurrencePattern pattern={patternDetail} />
        <TeamMembersAndSkills pattern={patternDetail} />
      </DetailsWrapper>
    </>
  )
}

const ButtonsWrapper = styled.div`
  display: flex;
  margin-left: auto;
  gap: 5px;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`
const DetailsWrapper = styled.div`
  display: flex;
  gap: 24px;
`
const TooltipTitle = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`
const StyledLink = styled(Link)`
  text-decoration: underline;
  color: ${(props) => props.theme.color.status.primary};
`
